import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { History, Lock, ArrowLeft, Truck, CheckCircle2, PackageCheck, Hourglass, PackagePlus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { NewLoadDialog } from '../../components/nursery/despatch/NewLoadDialog';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import type { LoadFieldsValue } from '../../components/nursery/despatch/LoadFields';
import type { BackorderList, BackorderOrder, NurseryDeliveryZone, NurseryLoadType } from '../../types/nursery';

/**
 * /nursery/backorders — every backorder still to go, and the quickest way to
 * send it (Rob, 2026-10-14). Each card says what stock is free for it now,
 * and where it goes next: a cross dock store's next pick day and its load in
 * one tap, or any open load, or a new one.
 *
 * Backorders are ahead of every other order for stock (the server's queue),
 * so "Allocate" here never takes trees from somebody further up the line.
 */

type Filter = 'all' | 'stock' | 'waiting' | 'on_load';

const LOAD_TYPE_FOR: Record<string, NurseryLoadType> = {
  run: 'run', carrier: 'carrier', post: 'post', collection: 'collection', cross_dock: 'cross_dock',
};

function Backorders() {
  const { t } = useTranslation('nursery', { keyPrefix: 'Backorders' });
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [data, setData] = useState<BackorderList | null>(null);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState<number | 'all' | null>(null);
  const [newLoadFor, setNewLoadFor] = useState<BackorderOrder | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, zoneRes] = await Promise.all([nurseryService.getBackorders(), nurseryService.getDeliveryZones()]);
      if (res.success) setData(res.data);
      else setError(errorMessage(res, t('loadFailed')));
      if (zoneRes.success) setZones(zoneRes.data || []);
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const orders = useMemo(() => data?.orders ?? [], [data]);
  const visible = useMemo(() => orders.filter((o) => {
    if (filter === 'on_load') return !!o.load_id;
    if (filter === 'stock') return !o.load_id && o.ready !== 'waiting';
    if (filter === 'waiting') return o.ready === 'waiting';
    return true;
  }), [orders, filter]);
  const counts = useMemo(() => ({
    all: orders.length,
    stock: orders.filter((o) => !o.load_id && o.ready !== 'waiting').length,
    waiting: orders.filter((o) => o.ready === 'waiting').length,
    on_load: orders.filter((o) => !!o.load_id).length,
  }), [orders]);

  const run = async (id: number | 'all', fn: () => Promise<string | null>) => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const message = await fn();
      if (message) setNotice(message);
      await load();
    } catch (e) {
      setError(errorMessage(e, t('actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const allocate = (ids: number[], key: number | 'all') => run(key, async () => {
    const res = await nurseryService.autoAllocate(ids);
    if (!res.success) throw res;
    if (res.data.no_places) return t('noPlaces');
    return t('allocated', { count: res.data.trees, n: res.data.trees.toLocaleString() });
  });

  const allocateAll = () => {
    const ids = orders.filter((o) => o.short_trees > 0 && o.can_fill_now > 0).map((o) => o.id);
    if (ids.length) allocate(ids, 'all');
  };

  const toCrossDock = (o: BackorderOrder) => run(o.id, async () => {
    const res = await nurseryService.putBackordersOnCrossDock([o.id]);
    if (!res.success) throw res;
    const [r] = res.data;
    if (!r?.placed) throw new Error(r?.message || t('actionFailed'));
    return t('placedOn', { order: o.order_number, load: r.load_number, date: r.date ? formatLocalDateInTz(r.date, tz) : '' });
  });

  const addToLoad = (o: BackorderOrder, loadId: number) => run(o.id, async () => {
    const res = await nurseryService.addOrdersToLoad(loadId, [o.id]);
    if (!res.success) throw res;
    return t('addedTo', { order: o.order_number, load: res.data.load_number });
  });

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenseTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="licenseBody" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/sales')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToSales')}
          </Button>
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const fillable = orders.filter((o) => o.short_trees > 0 && o.can_fill_now > 0).length;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<History className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/despatch')}>
            <Truck className="h-4 w-4" /> {t('planner')}
          </Button>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 break-words">{notice}</div>}

        {loading && !data ? (
          <div className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
        ) : s && (
          <>
            {s.orders === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>{t('none')}</span>
              </div>
            ) : (
              <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
                <p className="text-sm text-neutral-800">
                  {t('summary', { count: s.orders, trees: s.short_trees.toLocaleString() })}
                  {' '}
                  {s.can_fill_now > 0
                    ? <span className="font-medium text-green-700">{t('summaryFill', { trees: s.can_fill_now.toLocaleString() })}</span>
                    : <span className="text-neutral-500">{t('summaryNoStock')}</span>}
                </p>
                <p className="text-xs text-neutral-500">{t('priorityNote')}</p>
                {fillable > 0 && (
                  <Button size="sm" onClick={allocateAll} disabled={busyId !== null}>
                    <PackageCheck className="h-4 w-4" /> {t('allocateAll', { count: fillable })}
                  </Button>
                )}
              </div>
            )}

            {s.orders > 0 && (
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('filter')}>
                {(['all', 'stock', 'waiting', 'on_load'] as Filter[]).map((f) => (
                  <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}
                    className={cn('rounded-lg border px-3 py-1.5 text-sm',
                      filter === f ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium'
                        : 'bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50')}>
                    {t(`filters.${f}`, { n: counts[f] })}
                  </button>
                ))}
              </div>
            )}

            {visible.map((o) => (
              <BackorderCard
                key={o.id}
                order={o}
                tz={tz}
                today={today}
                openLoads={data?.open_loads ?? []}
                busy={busyId === o.id || busyId === 'all'}
                onAllocate={() => allocate([o.id], o.id)}
                onCrossDock={() => toCrossDock(o)}
                onAddToLoad={(loadId) => addToLoad(o, loadId)}
                onNewLoad={() => setNewLoadFor(o)}
              />
            ))}
            {s.orders > 0 && visible.length === 0 && (
              <p className="text-sm text-neutral-500 text-center py-8">{t('noneInFilter')}</p>
            )}
          </>
        )}
      </div>

      {newLoadFor && data && (
        <NewLoadDialog
          initial={newLoadInitial(newLoadFor, data, today)}
          orderIds={[newLoadFor.id]}
          trees={newLoadFor.total_trees}
          farms={data.farms}
          zones={zones}
          onClose={() => setNewLoadFor(null)}
          onCreated={(l) => navigate(`/nursery/loads/${l.id}`)}
        />
      )}
    </>
  );
}

function newLoadInitial(o: BackorderOrder, data: BackorderList, today: string): LoadFieldsValue {
  const farm = o.despatch_farm_id ?? (data.farms.length === 1 ? data.farms[0].id : 0);
  const due = o.despatch_from && o.despatch_from > today ? o.despatch_from : today;
  return {
    load_type: LOAD_TYPE_FOR[o.despatch_method] ?? 'run',
    despatch_farm_id: farm,
    despatch_date: due,
    zone_ids: o.delivery_zone_id ? [o.delivery_zone_id] : [],
  };
}

function ReadyBadge({ order: o }: { order: BackorderOrder }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'Backorders' });
  const cls = o.ready === 'allocated' || o.ready === 'can_fill' ? 'bg-green-100 text-green-800'
    : o.ready === 'part' ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-600';
  const Icon = o.ready === 'waiting' ? Hourglass : PackageCheck;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', cls)}>
      <Icon className="h-3 w-3" />
      {t(`ready.${o.ready}`, { n: o.can_fill_now.toLocaleString(), of: o.short_trees.toLocaleString() })}
    </span>
  );
}

function BackorderCard({ order: o, tz, today, openLoads, busy, onAllocate, onCrossDock, onAddToLoad, onNewLoad }: {
  order: BackorderOrder;
  tz: string;
  today: string;
  openLoads: BackorderList['open_loads'];
  busy: boolean;
  onAllocate: () => void;
  onCrossDock: () => void;
  onAddToLoad: (loadId: number) => void;
  onNewLoad: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'Backorders' });
  const [loadId, setLoadId] = useState<number | ''>('');
  // Loads it suits first: the same kind, then by date.
  const loads = useMemo(() => {
    const kind = LOAD_TYPE_FOR[o.despatch_method];
    return [...openLoads].sort((a, b) =>
      Number(b.load_type === kind) - Number(a.load_type === kind) || a.despatch_date.localeCompare(b.despatch_date));
  }, [openLoads, o.despatch_method]);
  const xd = o.cross_dock;

  return (
    <section className="bg-white border border-neutral-200 rounded-xl" aria-label={o.order_number}>
      <header className="px-4 pt-3 pb-2 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={`/nursery/orders/${o.id}`} className="font-semibold text-primary-700 hover:underline">{o.order_number}</Link>
          <span className="text-neutral-800 break-words min-w-0">{o.customer_name}</span>
          {o.customer_code && <span className="text-xs text-neutral-500">{t('store', { code: o.customer_code })}</span>}
          <ReadyBadge order={o} />
        </div>
        <p className="text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>
            <Trans t={t} i18nKey="backorderOf" values={{ order: o.parent_order_number }}
              components={{ a: <Link to={`/nursery/orders/${o.parent_order_id}`} className="text-primary-700 hover:underline" /> }} />
          </span>
          <span>{t(`method.${o.despatch_method}`)}</span>
          {o.customer_po && <span className="break-words min-w-0">{t('po', { po: o.customer_po })}</span>}
          {o.delivery_zone_name && o.despatch_method !== 'cross_dock' && <span className="break-words min-w-0">{o.delivery_zone_name}</span>}
        </p>
      </header>

      <ul className="px-4 divide-y divide-neutral-100 border-t border-neutral-100">
        {o.lines.map((l) => (
          <li key={l.id} className="py-2 flex items-start gap-3 text-sm">
            <span className="min-w-0 flex-1 break-words text-neutral-800">
              {l.item_name}{l.tree_type_name && <span className="text-neutral-500"> · {l.tree_type_name}</span>}
            </span>
            <span className="text-right text-xs whitespace-nowrap">
              <span className="block text-neutral-800 font-medium">
                {l.short > 0 ? t('lineShort', { n: l.short.toLocaleString() }) : t('lineAllocated', { n: l.quantity_allocated.toLocaleString() })}
              </span>
              {l.short > 0 && (
                <span className={cn('block', l.free_now > 0 ? 'text-green-700' : 'text-neutral-500')}>
                  {t('lineFree', { n: l.free_now.toLocaleString() })}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50 rounded-b-xl space-y-2">
        {o.short_trees > 0 && o.can_fill_now > 0 && (
          <Button size="sm" variant="outline" onClick={onAllocate} disabled={busy}>
            <PackageCheck className="h-4 w-4" /> {t('allocate', { n: o.can_fill_now.toLocaleString() })}
          </Button>
        )}

        {o.load_id ? (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-neutral-600">{t('onLoad')}</span>
            <Link to={`/nursery/loads/${o.load_id}`} className="font-medium text-primary-700 hover:underline">{o.load_number}</Link>
            {o.load_despatch_date && <span className="text-neutral-500">{formatLocalDateInTz(o.load_despatch_date, tz)}</span>}
            {o.load_status && <LoadStatusBadge status={o.load_status} />}
          </p>
        ) : o.despatch_method === 'cross_dock' ? (
          xd && xd.ok && xd.date ? (
            <div className="space-y-2">
              <p className="text-sm text-neutral-700 break-words">
                {t(xd.date === today ? 'nextPickToday' : 'nextPick', {
                  date: formatLocalDateInTz(xd.date, tz), day: xd.day_name ?? '', route: xd.route || t('noRoute'),
                })}
                {' '}
                {xd.load
                  ? (xd.load.addable ? t('loadWaiting', { load: xd.load.load_number }) : t('loadClosed', { load: xd.load.load_number }))
                  : t('noLoadYet')}
              </p>
              <Button size="sm" onClick={onCrossDock} disabled={busy}>
                <Truck className="h-4 w-4" />
                {xd.load?.addable ? t('addToXdock', { load: xd.load.load_number }) : t('putOnXdock', { date: formatLocalDateInTz(xd.date, tz) })}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-amber-800 break-words">{xd?.message || t('noPickDay')}</p>
          )
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {loads.length > 0 && (
              <>
                <select aria-label={t('addToLoadLabel', { order: o.order_number })} value={loadId}
                  onChange={(e) => setLoadId(e.target.value ? Number(e.target.value) : '')}
                  className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm w-full sm:w-72 min-w-0">
                  <option value="">{t('chooseLoad')}</option>
                  {loads.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.load_number} · {t(`loadType.${l.load_type}`)} · {formatLocalDateInTz(l.despatch_date, tz)}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="outline" disabled={!loadId || busy} onClick={() => loadId && onAddToLoad(loadId)}>
                  {t('add')}
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={onNewLoad} disabled={busy}>
              <PackagePlus className="h-4 w-4" /> {t('newLoad')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

export default Backorders;
